import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import classes from '@nerdwallet/base-styles/classes';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import property from 'lodash/property';
import classNames from 'classnames';
import { compose } from 'redux';
import { Accent, Box } from '@nerdwallet/currency';
import withDataDependencies from '@nerdwallet/structured-content/with-data-dependencies';
import { withMarketplaceContext } from '@nerdwallet/structured-content/contexts/MarketplaceContext';
import TextBox from '../text-box/text-box';
import SmartButton, { NULL_BUTTON_TYPE } from '../smart-button/smart-button';
import validateInterpolation from '../text-box/validate-interpolation';

import {
  stringTextAreaSpec,
  stringSelectSpec,
  boolCheckboxSpec,
  dataDependencyTypePicker,
  dataDependencyNumericIdInput,
  anchorIdInput,
} from '../../lib/editing-configs';
import getTextColor from '../../lib/text-color-config';
import BackgroundColor from '../background-color/background-color';

import withOutOfDateMessaging from '../with-out-of-date-messaging/with-out-of-date-messaging';

import './paragraph.less';

const Paragraph = ({
  preheader,
  header,
  headingStyle,
  alignment,
  mobileAlignment,
  body,
  mobileBody,
  anchorId,
  anchor, // deprecated
  paragraphClass,
  bodyClass,
  smallText,
  size,
  dangerouslyAllowHTMLIn: { header: htmlInHeader, body: htmlInBody },
  fontChronicle,
  data,
  interpolateFrom,
  fullWidthAll,
  fullWidthMobile,
  bgOptions,

  // From SmartButton - use verbatim to allow bringing in propTypes and editingConfig
  buttonType,
  textColorOptions,
  ...smartButtonProps
}) => {
  const offers = Object.values(_.get(data, 'value', {}));
  const interpolationSource = _.get(offers, 0, interpolateFrom);

  const button = (
    <SmartButton
      {...smartButtonProps}
      buttonType={buttonType}
      buttonClass="paragraph__button"
    />
  );

  const paragraphClassNameWidthFilter = fullWidthAll
    ? 'paragraph--100-all'
    : 'paragraph--100';

  const paragraphClassName =
    fullWidthAll || fullWidthMobile
      ? paragraphClassNameWidthFilter
      : 'paragraph';

  const { textColor } = getTextColor(textColorOptions);
  const colorStyle = { color: textColor };

  return validateInterpolation({
    interpolationSource,
    templates: { preheader, header, body },
  }) ? (
    <BackgroundColor bgOptions={bgOptions}>
      {(backgroundColor) => (
        <Box id="paragraph-background-wrapper" style={{ backgroundColor }}>
          <div
            id={anchorId || anchor /* for backwards compatability */}
            className={classNames(`${paragraphClassName}`, paragraphClass, {
              'paragraph-anchor': anchorId || anchor, // for backwards compatability
              [classes('padding-top-2')]: !fullWidthAll,
            })}
          >
            {preheader && (
              <Box className="paragraph-preheader" style={colorStyle}>
                <Accent size="small">{preheader}</Accent>
              </Box>
            )}
            {header && (
              <Box className={classes('padding-bottom-3')}>
                <TextBox
                  copyStyle={headingStyle}
                  textAlign={(size === 'sm' && mobileAlignment) || alignment}
                  interpolateFrom={interpolationSource}
                  dangerouslyAllowHTML={htmlInHeader}
                  text={header}
                  parseFormatting
                  color={textColor}
                  isHexColor
                  fontChronicle={fontChronicle}
                />
              </Box>
            )}
            <TextBox
              className={bodyClass}
              copyStyle={smallText ? 'copy-p2' : 'copy-p1'}
              textAlign="left"
              text={(size === 'sm' && mobileBody) || body}
              parseLinks
              color={textColor}
              isHexColor
              interpolateFrom={interpolationSource}
              dangerouslyAllowHTML={htmlInBody}
            />
            {button && buttonType !== NULL_BUTTON_TYPE && (
              <div
                className={classNames([
                  'paragraph__button',
                  {
                    [classes('padding-vertical-3')]:
                      buttonType !== NULL_BUTTON_TYPE,
                  },
                ])}
              >
                {button}
              </div>
            )}
          </div>
        </Box>
      )}
    </BackgroundColor>
  ) : null;
};

Paragraph.propTypes = {
  preheader: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  header: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  alignment: PropTypes.string,
  mobileAlignment: PropTypes.string,
  body: PropTypes.node,
  mobileBody: PropTypes.node,
  headingStyle: PropTypes.string,
  paragraphClass: PropTypes.string,
  anchor: PropTypes.string,
  anchorId: PropTypes.string,
  dangerouslyAllowHTMLIn: PropTypes.shape({
    header: PropTypes.bool,
    body: PropTypes.bool,
  }),
  bodyClass: PropTypes.string,
  size: PropTypes.string,
  smallText: PropTypes.bool,
  fontChronicle: PropTypes.bool,
  data: PropTypes.shape({
    loading: PropTypes.bool,
    error: PropTypes.object,
    value: PropTypes.object,
  }),
  interpolateFrom: PropTypes.object,
  ...SmartButton.propTypes,
  fullWidthAll: PropTypes.bool,
  fullWidthMobile: PropTypes.bool,
};

Paragraph.defaultProps = {
  body: '',
  paragraphClass: '',
  headingStyle: 'copy-h3',
  alignment: 'left',
  mobileAlignment: '',
  dangerouslyAllowHTMLIn: {
    header: false,
    body: false,
  },
  bodyClass: '',
  smallText: false,
  size: '',
  fontChronicle: false,
  data: {},
  interpolateFrom: {},
  ...SmartButton.defaultProps,
  fullWidthAll: false,
  fullWidthMobile: false,
};

Paragraph.displayName = 'Paragraph';

Paragraph.getEditingConfig = (componentDocument) => [
  stringTextAreaSpec('preheader', 'Preheader'),
  stringTextAreaSpec('header', 'Header', {
    help: 'For GothamBlack font, surround title text with | like |this|',
  }),
  stringSelectSpec('alignment', 'Header Alignment', [
    { label: 'Left Align', value: 'left' },
    { label: 'Center Align', value: 'center' },
  ]),
  stringSelectSpec(
    'mobileAlignment',
    'Header Alignment (mobile only)',
    [
      { label: 'Same as Desktop', value: '' },
      { label: 'Left Align', value: 'left' },
      { label: 'Center Align', value: 'center' },
    ],
    {
      help: `If nothing is set, alignment will display the same on mobile and desktop`,
    }
  ),
  boolCheckboxSpec('fontChronicle', 'Header in Chronicle?'),
  ...getTextColor.getEditingConfig(componentDocument),
  ...BackgroundColor.getEditingConfig(componentDocument),
  stringSelectSpec('headingStyle', 'Heading Style', [
    { label: 'Select one', value: '' },
    { label: 'Heading 1', value: 'copy-h1' },
    { label: 'Heading 2', value: 'copy-h2' },
    { label: 'Heading 3', value: 'copy-h3' },
    { label: 'Heading 4', value: 'copy-h4' },
    { label: 'Heading 5', value: 'copy-h5' },
  ]),
  boolCheckboxSpec('dangerouslyAllowHTMLIn.header', 'Allow HTML in header?', {
    help: 'WARNING: We strongly advise against making use of this feature unless you really need it.',
  }),
  stringTextAreaSpec('body', 'Body', {
    help: `Want a hyperlink? Write it like this: [Hi!](www.thing.com).
           NW links should be relative, e.g., [Home](/home)`,
  }),
  stringTextAreaSpec('mobileBody', 'Body (mobile only)', {
    help: `If nothing is set, general body text will display on mobile and desktop`,
  }),
  boolCheckboxSpec('smallText', 'Use small text?'),
  boolCheckboxSpec('dangerouslyAllowHTMLIn.body', 'Allow HTML in body?', {
    help: 'WARNING: We strongly advise against making use of this feature unless you really need it.',
  }),
  boolCheckboxSpec('fullWidthAll', '100% width in ALL viewports?', {
    help: 'The Paragraph defaults to a width of 280px on mobile viewports, 960px on desktop viewports. Check this box to make all viewports 100% width instead. Please note: this setting has precedence over "100% width in mobile viewports" setting.',
  }),
  boolCheckboxSpec('fullWidthMobile', '100% width in mobile viewports?', {
    help: 'The Paragraph defaults to a width of 280px on mobile viewports. Check this box to make it 100% width for mobile viewports instead.',
  }),
  dataDependencyTypePicker,
  dataDependencyNumericIdInput,
  anchorIdInput,
  ...SmartButton.getEditingConfig(componentDocument),
];

const dataDependencies = (props) => {
  const type = _.get(props, 'dataDependency.type');

  if (!type) {
    return null;
  }

  return {
    data: {
      type,
      ids: _.get(props, 'dataDependency.data.ids', []).map((d) => d.id),
    },
  };
};

const WrappedComponent = compose(
  withMarketplaceContext,
  withOutOfDateMessaging,
  connect(
    createStructuredSelector({
      size: property('mediaQuery'),
    })
  )
)(Paragraph);

export default withDataDependencies(WrappedComponent, dataDependencies);
